<template>
    <div class="wallet-block">
        <div class="wallet-block-label">Баланс</div>

        <!-- Круговой прогресс-бар -->
        <div class="circular-progress-container">
            <svg :width="size" :height="size" class="progress-svg">
                <!-- Фоновый круг -->
                <circle
                    :cx="size/2"
                    :cy="size/2"
                    :r="radius"
                    stroke="rgba(255, 255, 255, 0.1)"
                    :stroke-width="strokeWidth"
                    fill="none"
                />
                <!-- Прогресс (убывающий) -->
                <circle
                    :cx="size/2"
                    :cy="size/2"
                    :r="radius"
                    :stroke="progressColor"
                    :stroke-width="strokeWidth"
                    fill="none"
                    :stroke-dasharray="circumference"
                    :stroke-dashoffset="strokeDashoffset"
                    stroke-linecap="round"
                    class="progress-circle"
                />
            </svg>
            
            <!-- Текст внутри круга -->
            <div class="balance-text">
                <div class="current-balance">{{ formattedBalance }}₽</div>
                <div class="total-balance">из {{ formattedTotalBalance }}₽</div>
                <div class="remaining-balance" :class="{ 'low-balance': isLowBalance }">
                    Осталось: {{ progressPercentage }}%
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/api/api';

export default {
    data() {
        return {
            user_balance: 0, // Сколько осталось (большая цифра)
            user_total_balance: 0, // Всего было доступно (меньшая цифра)
            size: 120,
            strokeWidth: 10
        };
    },

    computed: {
        radius() {
            return (this.size - this.strokeWidth) / 2;
        },
        
        circumference() {
            return 2 * Math.PI * this.radius;
        },
        
        // Прогресс (от 0 до 1), где 1 - полный круг (все деньги остались), 0 - пусто (все потрачено)
        progress() {
            if (this.user_total_balance === 0) return 0;
            return Math.max(0, this.user_balance / this.user_total_balance);
        },
        
        // Процент оставшихся средств
        progressPercentage() {
            return Math.round(this.progress * 100);
        },
        
        // Смещение для SVG (убывающий прогресс)
        strokeDashoffset() {
            return this.circumference - (this.progress * this.circumference);
        },
        
        // Цвет прогресса в зависимости от остатка
        progressColor() {
            if (this.progress > 0.5) return '#4CAF50'; // Зеленый - много осталось
            if (this.progress > 0.2) return '#FFA726'; // Оранжевый - средний остаток
            return '#F44336'; // Красный - мало осталось
        },
        
        // Форматированные значения для отображения
        formattedBalance() {
            return this.formatCurrency(this.user_balance);
        },
        
        formattedTotalBalance() {
            return this.formatCurrency(this.user_total_balance);
        },
        
        // Проверка на низкий баланс
        isLowBalance() {
            return this.progress <= 0.2;
        }
    },

    mounted() {
        this.loadProfileData();
    },

    methods: {
        async loadProfileData() {
            try {
                const tg_id = localStorage.getItem('tg_id');
                
                if (!tg_id) {
                    console.error('tg_id не найден в localStorage');
                    return;
                }

                const profileId = parseInt(tg_id);
                const response = await api.get(`/drop/profile/balance/${profileId}`);
                
                console.log('Данные профиля получены:', response.data);
                this.user_balance = response.data.user_balance; // Сколько осталось
                this.user_total_balance = response.data.user_total_balance; // Всего было
                
            } catch (error) {
                console.error('Ошибка при загрузке данных профиля:', error);
                
                if (error.response && error.response.status === 403) {
                    console.log('Пользователь не является дроп-пользователем');
                }
            }
        },
        
        // Форматирование валюты
        formatCurrency(amount) {
            return new Intl.NumberFormat('ru-RU').format(amount);
        }
    },
};
</script>

<style scoped>
.wallet-block {
    font-family: 'CentureGothic', sans-serif;
    box-sizing: border-box;
    padding: 2vw;
    background-color: rgba(255, 255, 255, 0.08);
    height: 100%;
    width: 100%;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
}

.wallet-block-label {
    width: 100%;
    text-align: center;
    color: white;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 1vw;
}

.circular-progress-container {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin: 0;
    flex: 1;
}

.progress-svg {
    transform: rotate(-90deg);
    max-width: 100%;
    height: auto;
}

.progress-circle {
    transition: all 0.5s ease;
    filter: drop-shadow(0 0 6px currentColor);
}

.balance-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    text-align: center;
    color: white;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.2vw;
    width: 90%;
}

.current-balance {
    font-size: 24px;
    color: #005ADA;
    font-weight: 900;
    font-style: italic;
    line-height: 1.2;
}

.total-balance {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
    line-height: 1.1;
}

.remaining-balance {
    font-size: 11px;
    color: #4CAF50;
    font-weight: 600;
    padding: 0.3vw 1vw;
    background: rgba(76, 175, 80, 0.1);
    border-radius: 12px;
    transition: all 0.3s ease;
    text-align: center;
    line-height: 1.3;
    margin-top: 0.5vw;
}

.remaining-balance.low-balance {
    color: #F44336;
    background: rgba(244, 67, 54, 0.1);
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.7; }
    100% { opacity: 1; }
}

/* Адаптивность для мобильных устройств */
@media (max-width: 768px) {
    .wallet-block {
        padding: 3vw;
        min-height: 180px;
    }
    
    .current-balance {
        font-size: 20px;
    }
    
    .total-balance {
        font-size: 12px;
    }
    
    .remaining-balance {
        font-size: 10px;
    }
}

@media (max-width: 480px) {
    .wallet-block {
        padding: 4vw;
        min-height: 160px;
    }
    
    .current-balance {
        font-size: 18px;
    }
    
    .total-balance {
        font-size: 11px;
    }
    
    .remaining-balance {
        font-size: 9px;
        padding: 0.4vw 1.2vw;
    }
    
    .wallet-block-label {
        font-size: 12px;
        margin-bottom: 2vw;
    }
}

/* Гарантия что круг не выходит за границы */
.circular-progress-container {
    max-width: 100%;
    max-height: 100%;
}

.progress-svg {
    display: block;
    margin: 0 auto;
}
</style>